/** @jsxImportSource theme-ui */
import { FastField, FastFieldProps } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import { Label } from 'semantic-ui-react';

import LocationApi from 'api/LocationApi';
import { DefaultMaxUserLocations } from 'utils/constants';
import { sxtheme } from 'utils/cssHelpers';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { BdParagraph } from 'components/_common/SectionHeader';
import { Typography } from 'components/_common/Typography';
import { BdAdditiveLocationSearch } from 'components/_controls/AoiSearch/BdAdditiveLocationSearch';
import { LocationSearchResult } from 'components/_controls/AoiSearch/models';

import metadataSelectors from 'metadata/metadata.selectors';

import BdChip from '../../components/_controls/BdChip/BdChip';
import { SemanticFormikProps } from '../../components/SidebarView/SettingsView/FormikControls/FormikSemanticProps';
import { LocationSelectionItem } from './LocationsModel';

export type LocationMultiSelectFormikControlProps = SemanticFormikProps & {
  errorMessage?: string;
};

export const LocationMultiSelectFormikControl: React.FC<LocationMultiSelectFormikControlProps> = ({
  name,
  error,
  errorMessage,
}) => {
  const appMetadata = useSelector(metadataSelectors.getMetadata);
  const maxAois = appMetadata?.maxUserLocations ?? DefaultMaxUserLocations;

  const PageParagraph: React.FC = ({ children }) => (
    <BdParagraph>
      <Typography variant="body1" color="stone90">
        {children}
      </Typography>
    </BdParagraph>
  );

  return (
    <div data-testid="location-selection-multiselect">
      <PageParagraph>
        See your default locations within the Custom View on your dashboard and Risk Matrix. You
        will also receive notifications about risk to these locations.
      </PageParagraph>
      <PageParagraph>Select up to {formatNumber(maxAois, 'location')}:</PageParagraph>
      <PageParagraph>
        <FastField name={name}>
          {({ field, form, meta }: FastFieldProps) => {
            const { value } = field;
            const locations = value as LocationSelectionItem[];

            const addLocation = (newLocation: LocationSearchResult) => {
              form.setFieldTouched(name, true);
              form.setFieldValue(name, [
                ...locations,
                {
                  locationId: newLocation.id,
                  name: newLocation.displayName,
                },
              ]);
            };

            const removeLocation = (locationId: number) => {
              const removeIndex = locations.map(item => item.locationId).indexOf(+locationId);

              if (removeIndex !== -1) {
                locations.splice(removeIndex, 1);
              }
              form.setFieldTouched(name, true);
              form.setFieldValue(name, locations);
            };

            return (
              <>
                <BdAdditiveLocationSearch
                  onSearchApiCall={LocationApi.searchLocations}
                  selectedItemIds={locations?.map(x => x.locationId)}
                  onAddItem={addLocation}
                  error={meta?.error ?? null}
                />
                <div
                  sx={{
                    pt: '16px',
                    overflowY: 'auto',
                  }}
                >
                  {error && errorMessage && (
                    <div
                      sx={{
                        width: 'fit-content',
                        color: sxtheme(t => t.colors.clay100),
                        background: sxtheme(t => t.colors.clay20),
                        border: theme => `1px solid ${theme.colors.clay100}`,
                        borderRadius: '3px',
                        p: '8px',
                        mb: '16px',
                      }}
                    >
                      <Typography variant="subtitle2">{errorMessage}</Typography>
                    </div>
                  )}
                  <Label.Group>
                    {locations &&
                      locations.map(x => (
                        <BdChip
                          key={x.locationId}
                          id={`location-${x.locationId}`}
                          label={x.name}
                          onDelete={() => removeLocation(x.locationId)}
                        />
                      ))}
                  </Label.Group>
                </div>
              </>
            );
          }}
        </FastField>
      </PageParagraph>
    </div>
  );
};
